import { useState, useEffect, useCallback, useRef } from 'react';

export default function useTimer(onExpire) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!isRunning || secondsLeft <= 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (isRunning && secondsLeft <= 0) {
        setIsRunning(false);
        onExpireRef.current?.();
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setIsRunning(false);
          onExpireRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [isRunning, secondsLeft]);

  const start = useCallback((totalSeconds) => {
    setSecondsLeft(totalSeconds);
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    setSecondsLeft(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const deduct = useCallback((secs) => {
    setSecondsLeft(prev => Math.max(0, prev - secs));
  }, []);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const display = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  const isLow = secondsLeft > 0 && secondsLeft <= 60;

  return { secondsLeft, isRunning, start, stop, reset, deduct, display, minutes, seconds, isLow };
}
